import React, { useCallback, useMemo } from "react"
import { Col, Row } from "react-bootstrap"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { News, useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useLanguageState } from "../../globalStates/LanguageState"
import { ReferenceDataType, withReferenceDataState } from "../../globalStates/ReferenceDataHOC"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { hasErrorsAll, makeSetHandlerPlain, makeValidationHandler } from "../../globalStates/utils"
import TextField from "../../components/Field/Text"
import TextAreaField from "../../components/Field/TextArea"
import DateTimeField from "../../components/Field/DateTime"
import FilePicker from "../../components/FilePicker"
import PageContents from "../../components/PageContents"
import Section from "../../components/Section"
import { Tabs, Tab } from "../../components/Tabs"
import {
    fieldNameByLang,
    getElementFieldValueWithLanguage,
    makeSetHandlerWithLanguage,
    makeValidationHandlerWithLanguage,
    useLanguageSwitch
} from "../../utils/Language"
import { hasServiceKeys } from "../../globalStates/ConfigDataState"
import CategoriesContents from "../categories/CategoriesContents"
import MultimediaContents from "../multimedia/MultimediaContents"
import KeywordContents from "../profile/KeywordContents"
import LanguageSwitch from "../languageSwitch"

interface NewsSectionProps {
    newsState: State<News, Validation>
    index: number
    onClickRemove: (productId: string) => void
}

const NewsSection: React.FC<NewsSectionProps & ReferenceDataType> = ({ newsState, refDataState, index, onClickRemove }) => {
    const branding = useLanguageState().getStrings()
    const exiState = useExhibitorDataState()
    const news = useHookstate(newsState)
    const isMonoLingual = branding.configuration.monoLingual ? "De" : "En"
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual)
    const serviceKeys = useConfigDataState().getServiceKeys()
    const refCategories = useMemo(
        () => refDataState.getCategories("NEWS", branding.configuration.newsPage.sctNews.categoryWhitelist),
        [branding.configuration.newsPage.sctNews.categoryWhitelist, refDataState]
    )
    const categoryVisible = serviceKeys.category ? serviceKeys.category > 0 && refCategories.length > 0 : false
    const multimediaVisible = hasServiceKeys(serviceKeys.multimedia)
    const keywordsVisible = hasServiceKeys(serviceKeys.keywords)

    const onChangeLogo = useCallback(
        (image: File, onUploadProgress, onSuccessCallback, onErrorCallback) => {
            exiState
                .upload("LOGO", image, onUploadProgress)
                .then((response) => {
                    onSuccessCallback()
                    if (response) {
                        news.logoFileId.set(response.fileId)
                        news.logoUrl.set(response.fileUrl)
                    }
                })
                .catch((reason) => onErrorCallback(reason))
        },
        [exiState, news]
    )

    const onRemoveLogo = useCallback(() => {
        news.logoFileId.set("")
        news.logoUrl.set("")
    }, [news])

    const onClickRemoveHandler = useCallback(() => {
        onClickRemove(news.id.get())
    }, [onClickRemove, news])

    return (
        <Section
            title={news.name.get() || news.nameDe.get() || branding.configuration.newsPage.sctNews.titleEmptyNews}
            collapsible
            onClickRemove={onClickRemoveHandler}
            entityState={news}
        >
            <Row>
                <Col xs={3}>
                    <FilePicker
                        name="companyLogo"
                        label={branding.configuration.newsPage.sctNews.lblNewsImage}
                        message={branding.configuration.newsPage.sctNews.lblNewsImageMessage}
                        browseLabel={branding.configuration.newsPage.sctNews.btnBrowseFiles}
                        deleteLabel={branding.configuration.newsPage.sctNews.btnDeleteImg}
                        placeholderTitle={branding.configuration.newsPage.sctNews.hntTitle}
                        placeholderText={branding.configuration.newsPage.sctNews.hntProp}
                        value={news.logoUrl.get()}
                        onChangeImage={onChangeLogo}
                        onRemoveImage={onRemoveLogo}
                    />
                </Col>
                <Col xs={9}>
                    <Row>
                        <Col>
                            <LanguageSwitch
                                id={`news_${index}_language_switch`}
                                name={`news_${index}_languages`}
                                value={currentLanguage}
                                onChange={onChangeLanguageHandler}
                                entityState={newsState}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextField
                                id={fieldNameByLang("name", currentLanguage)}
                                name={fieldNameByLang("name", currentLanguage)}
                                label={branding.configuration.newsPage.sctNews.lblNewsTitle}
                                placeholder={branding.configuration.newsPage.sctNews.hntNewsTitle}
                                validate={makeValidationHandlerWithLanguage(news, "name", currentLanguage)}
                                value={getElementFieldValueWithLanguage(news, "name", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(news, "name", currentLanguage)}
                                required
                            />
                        </Col>
                        <Col>
                            <DateTimeField
                                id={fieldNameByLang("date", currentLanguage)}
                                name={fieldNameByLang("date", currentLanguage)}
                                label={branding.configuration.newsPage.sctNews.lblNewsDate}
                                placeholder={branding.configuration.newsPage.sctNews.hntNewsDate}
                                validate={makeValidationHandler(news, "date")}
                                value={news.date.get()}
                                onChange={(datetime: Date) => {
                                    news.date.set(datetime)
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("description", currentLanguage)}
                                label={branding.configuration.newsPage.sctNews.lblDescription}
                                placeholder={branding.configuration.newsPage.sctNews.hntDescription}
                                value={getElementFieldValueWithLanguage(news, "description", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(news, "description", currentLanguage)}
                                limit={4000}
                                style={{ height: "100px" }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <TextAreaField
                                name={fieldNameByLang("teaser", currentLanguage)}
                                label={branding.configuration.newsPage.sctNews.lblSubtitle}
                                placeholder={branding.configuration.newsPage.sctNews.hntSubtitle}
                                value={getElementFieldValueWithLanguage(news, "teaser", currentLanguage)}
                                onChange={makeSetHandlerWithLanguage(news, "teaser", currentLanguage)}
                                limit={300}
                                style={{ height: "80px" }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            {/*TODO get rid of these */}
            <br />
            <br />
            {(categoryVisible || multimediaVisible || keywordsVisible) && (
                <Row>
                    <Col xs={12}>
                        <Tabs>
                            {categoryVisible && (
                                <Tab eventKey="categories" title={branding.configuration.newsPage.sctNews.lblTabCategories}>
                                    <PageContents description={branding.configuration.newsPage.sctNews.lblCategoriesDescription}>
                                        <CategoriesContents
                                            selectedCategoriesStates={news.categories}
                                            refCategories={refCategories}
                                            limit={serviceKeys.category}
                                            countableCategories={
                                                branding.configuration.countableCategories.jobCountableCategories
                                            }
                                        />
                                    </PageContents>
                                </Tab>
                            )}
                            <Tab
                                eventKey="multimedia"
                                title={branding.configuration.newsPage.sctNews.lblTabMultimedia}
                                tabClassName={hasErrorsAll(news.medias) ? "invalid" : ""}
                            >
                                <PageContents description={branding.configuration.newsPage.sctNews.lblMultimediaDescription}>
                                    <MultimediaContents
                                        mediaStates={news.medias}
                                        index={index}
                                        multimediaLimit={branding.configuration.newsPage.sctNews.multimediaLimit}
                                    />
                                </PageContents>
                            </Tab>
                            {keywordsVisible && (
                                <Tab eventKey="keywords" title={branding.configuration.newsPage.sctNews.lblTabKeywords}>
                                    <PageContents description="">
                                        <KeywordContents value={news.tags.get()} onChange={makeSetHandlerPlain(news, "tags")} />
                                    </PageContents>
                                </Tab>
                            )}
                        </Tabs>
                    </Col>
                </Row>
            )}
        </Section>
    )
}

export default React.memo(withReferenceDataState(NewsSection))
